import { ElementStyleService } from "../../../services/element-style.service";
import {
    IButtonBaseStyle,
    IFontData,
    IPricingPlanBaseStyle,
    IPricingPlanHighlightedStyle,
    IPricingTableWidgetData,
    IPricingTableWidgetStyle, IRibbonStyle,
    ISwitcherStyle
} from "@pageBuilder/typings";
import { CssProperties } from "../../../models/css-property";

export class PricingTableWidgetStyleRenderer {

    constructor(private readonly styleService: ElementStyleService) {

    }

    public render(element: HTMLElement, data: IPricingTableWidgetData): void {
        this.renderLayoutElementStyles(element, data.style);
        this.renderSwitcherStyles(element, data.style.switcherStyle);
        this.renderPricingPlansStyles(element, data);
    }

    public getContentSelectors(element: HTMLElement): string[] {
        const selectors: string[] = [];

        selectors.push(this.layoutSelector(element.id));
        selectors.push(this.layoutRowSelector(element.id));
        selectors.push(this.pricingTableSelector(element.id));
        selectors.push(this.switcherSelector(element.id));
        selectors.push(this.switcherActiveContentSelector(element.id));
        selectors.push(this.switcherActiveItemSelector(element.id));

        const pricingPlans = element.querySelectorAll(".layout .row .pricing-plan");
        pricingPlans.forEach(pricingPlan => {
            const number = pricingPlan.getAttribute("data-number");
            if (number) {
                selectors.push(this.pricingPlanSelector(element.id, number));
                selectors.push(this.pricingPlanBackgroundSelector(element.id, number));
                selectors.push(this.nameSelector(element.id, number));
                selectors.push(this.costSelector(element.id, number));
                selectors.push(this.periodSelector(element.id, number));
                selectors.push(this.periodIndicatorSelector(element.id, number));
                selectors.push(this.descriptionSelector(element.id, number));
                selectors.push(this.mainFeaturesSelector(element.id, number));
                selectors.push(this.ribbonSelector(element.id, number));
                selectors.push(this.buttonSelector(element.id, number));
                selectors.push(this.buttonSelector(element.id, number, true));
            }
        });

        return selectors;
    }

    private renderLayoutElementStyles(element: HTMLElement, style: IPricingTableWidgetStyle): void {
        const layoutStyleProps = new CssProperties();

        layoutStyleProps.tryAdd("font-family", style.font.family);
        layoutStyleProps.tryAdd("font-weight", style.font.weight);

        this.styleService.setStyle(element, this.layoutSelector(element.id), layoutStyleProps.data);
        this.renderLayoutGap(element, style);
    }

    private renderLayoutGap(element: HTMLElement, style: IPricingTableWidgetStyle): void {
        const rowLayoutStyleProps = new CssProperties();
        const columnStyleProps = new CssProperties();

        rowLayoutStyleProps.add("gap", `${style.spaceBetweenRows}px`);
        columnStyleProps.add("gap", `${style.spaceBetweenItems}px`);

        this.styleService.setStyle(element, this.layoutRowSelector(element.id), columnStyleProps.data);
        this.styleService.setStyle(element, this.pricingTableSelector(element.id), rowLayoutStyleProps.data);
    }

    private renderSwitcherStyles(element: HTMLElement, style: ISwitcherStyle): void {
        const styleProps = new CssProperties();
        const activeStyleProps = new CssProperties();

        if (style.type === "rectangle") {
            styleProps.add("border-radius", "4px");
            activeStyleProps.add("border-radius", "4px");
        } else if (style.type === "oval") {
            styleProps.add("border-radius", "100px");
            activeStyleProps.add("border-radius", "100px");
        }

        styleProps.tryAdd("background-color", style.backgroundColor);
        styleProps.tryAdd("color", style.textColor);
        styleProps.tryAdd("font-size", style.font?.size);
        styleProps.tryAdd("font-weight", style.font?.weight);

        activeStyleProps.tryAdd("background-color", style.switchColor);
        activeStyleProps.tryAdd("color", style.switchTextColor);
        activeStyleProps.tryAdd("font-size", style.font?.size);
        activeStyleProps.tryAdd("font-weight", style.font?.weight);

        this.styleService.setStyle(element, this.switcherSelector(element.id), styleProps.data);
        this.styleService.setStyle(element, this.switcherActiveContentSelector(element.id), activeStyleProps.data);
        this.styleService.setStyle(element, this.switcherActiveItemSelector(element.id), activeStyleProps.data);
    }

    private renderPricingPlansStyles(element: HTMLElement, data: IPricingTableWidgetData): void {
        data.pricingPlans.forEach((plan, index) => {
            if (plan.theme === "highlighted") {
                this.renderPricingPlanHighlightedStyle(element, data.style.highlightedThemeStyle, `${index+1}`);
            } else {
                this.renderPricingPlanBaseStyle(element, data.style.defaultThemeStyle, `${index+1}`);
            }
        });
    }

    private renderPricingPlanBaseStyle(element: HTMLElement, style: IPricingPlanBaseStyle, planNumber: string): void {
        this.renderPricingPlanCommonStyles(element, style, planNumber);
        this.renderPricingPlanBackgroundStyle(element, style, planNumber);
        this.renderPricingPlanContentFonts(element, style, planNumber);
        this.renderPricingPlanButtonStyles(element, planNumber, style.buttonStyle);
    }

    private renderPricingPlanHighlightedStyle(element: HTMLElement, style: IPricingPlanHighlightedStyle, planNumber: string): void {
        this.renderPricingPlanBaseStyle(element, style, planNumber);
        this.renderRibbonStyle(element, style.ribbonStyle, planNumber);
    }

    private renderPricingPlanCommonStyles(element: HTMLElement, style: IPricingPlanBaseStyle, pricingPlanNumber: string): void {
        const styleProps = new CssProperties();

        const padding = style.padding + style.borderThickness;
        styleProps.tryAdd("padding", `${padding}px`);

        this.styleService.setStyle(element, this.pricingPlanSelector(element.id, pricingPlanNumber), styleProps.data);
    }

    private renderPricingPlanBackgroundStyle(element: HTMLElement, style: IPricingPlanBaseStyle, pricingPlanNumber: string): void {
        const styleProps = new CssProperties();

        if (style.borderRadius) {
            styleProps.tryAdd("border-top-left-radius", `${style.borderRadius.topLeftPx}px`);
            styleProps.tryAdd("border-top-right-radius", `${style.borderRadius.topRightPx}px`);
            styleProps.tryAdd("border-bottom-left-radius", `${style.borderRadius.bottomLeftPx}px`);
            styleProps.tryAdd("border-bottom-right-radius", `${style.borderRadius.bottomRightPx}px`);
        }

        if (style.background) {
            styleProps.tryAdd("background-color", style.background.color);
        }

        styleProps.tryAdd("border", `${style.borderThickness}px solid`);
        styleProps.tryAdd("border-color", style.borderColor);

        this.styleService.setStyle(element, this.pricingPlanBackgroundSelector(element.id, pricingPlanNumber), styleProps.data);
    }

    private renderPricingPlanContentFonts(element: HTMLElement, style: IPricingPlanBaseStyle, pricingPlanNumber: string): void {
        if (style.nameFont) {
            this.renderFontStyle(element, style.nameFont, this.nameSelector(element.id, pricingPlanNumber));
        }

        if (style.costFont) {
            this.renderFontStyle(element, style.costFont, this.costSelector(element.id, pricingPlanNumber));
        }

        if (style.periodFont) {
            this.renderFontStyle(element, style.periodFont, this.periodSelector(element.id, pricingPlanNumber));
            this.renderFontStyle(element, style.periodFont, this.periodIndicatorSelector(element.id, pricingPlanNumber));
        }

        if (style.descriptionFont) {
            this.renderFontStyle(element, style.descriptionFont, this.descriptionSelector(element.id, pricingPlanNumber));
        }

        if (style.mainFeaturesFont) {
            this.renderFontStyle(element, style.mainFeaturesFont, this.mainFeaturesSelector(element.id, pricingPlanNumber));
        }
    }

    private renderPricingPlanButtonStyles(element: HTMLElement, pricingPlanNumber: string, style?: IButtonBaseStyle | null): void {
        if (!style) {
            return;
        }

        const styleProps = new CssProperties();
        const hoverStyleProps = new CssProperties();

        styleProps.tryAdd("border-style", "solid");
        styleProps.tryAdd("border-width", `${style.thickness}`);

        if (style.borderRadius) {
            styleProps.tryAdd("border-top-left-radius", `${style.borderRadius.topLeftPx}px`);
            styleProps.tryAdd("border-top-right-radius", `${style.borderRadius.topRightPx}px`);
            styleProps.tryAdd("border-bottom-left-radius", `${style.borderRadius.bottomLeftPx}px`);
            styleProps.tryAdd("border-bottom-right-radius", `${style.borderRadius.bottomRightPx}px`);
        }

        styleProps.tryAdd("font-size", style.font?.size);

        styleProps.tryAdd("background-color", style.backgroundColor);
        hoverStyleProps.tryAdd("background-color", style.backgroundHoverColor);

        styleProps.tryAdd("border-color", style.borderColor);
        hoverStyleProps.tryAdd("border-color", style.borderHoverColor);

        styleProps.tryAdd("color", style.color);
        hoverStyleProps.tryAdd("color", style.hoverColor);

        this.styleService.setStyle(element, this.buttonSelector(element.id, pricingPlanNumber), styleProps.data);
        this.styleService.setStyle(element, this.buttonSelector(element.id, pricingPlanNumber, true), hoverStyleProps.data);
    }

    private renderRibbonStyle(element: HTMLElement, style: IRibbonStyle, pricingPlanNumber: string): void {
        const styleProps = new CssProperties();
        styleProps.tryAdd("background-color", style.backgroundColor);

        if (style.borderRadius) {
            styleProps.tryAdd("border-top-left-radius", `${style.borderRadius.topLeftPx}px`);
            styleProps.tryAdd("border-top-right-radius", `${style.borderRadius.topRightPx}px`);
            styleProps.tryAdd("border-bottom-left-radius", `${style.borderRadius.bottomLeftPx}px`);
            styleProps.tryAdd("border-bottom-right-radius", `${style.borderRadius.bottomRightPx}px`);
        }

        if (style.font) {
            styleProps.tryAdd("font-size", style.font.size);
            styleProps.tryAdd("color", style.font.color);
        }

        if (style.buttonAlignment) {
            styleProps.tryAdd("text-align", style.buttonAlignment);
            styleProps.tryAdd("justify-content", style.buttonAlignment);
        }

        return this.styleService.setStyle(element, this.ribbonSelector(element.id, pricingPlanNumber), styleProps.data);
    }

    private renderFontStyle(element: HTMLElement, font: IFontData, selector: string): void {
        const fontStyleProps = new CssProperties();

        fontStyleProps.tryAdd("font-family", font.family);
        fontStyleProps.tryAdd("font-weight", font.weight);
        fontStyleProps.tryAdd("font-size", font.size);
        fontStyleProps.tryAdd("color", font.color);

        this.styleService.setStyle(element, selector, fontStyleProps.data);
    }

    private layoutSelector(elementId: string): string {
        return `#${elementId} .widget-content .layout`;
    }

    private switcherSelector(elementId: string): string {
        return `#${elementId} .widget-content .layout .switcher-wrapper .switcher`;
    }

    private switcherActiveItemSelector(elementId: string): string {
        return `#${elementId} .widget-content .layout .switcher-wrapper .switcher .switcher-item.active`;
    }

    private switcherActiveContentSelector(elementId: string): string {
        return `#${elementId} .widget-content .layout .switcher-wrapper .switcher::before`;
    }

    private layoutRowSelector(elementId: string): string {
        return `#${elementId} .widget-content .layout .row`;
    }

    private pricingTableSelector(elementId: string): string {
        return `#${elementId} .widget-content .layout .pricing-table`;
    }

    private pricingPlanSelector(elementId: string, pricingPlanNumber: string): string {
        return `#${elementId} .widget-content .layout .pricing-plan[data-number="${pricingPlanNumber}"]`;
    }

    private pricingPlanBackgroundSelector(elementId: string, pricingPlanNumber: string): string {
        return `#${elementId} .widget-content .layout .pricing-plan[data-number="${pricingPlanNumber}"] .background`;
    }

    private nameSelector(elementId: string, pricingPlanNumber: string): string {
        return `${this.pricingPlanSelector(elementId, pricingPlanNumber)} .plan-header .name`;
    }

    private costSelector(elementId: string, pricingPlanNumber: string): string {
        return `${this.pricingPlanSelector(elementId, pricingPlanNumber)} .plan-header .cost`;
    }

    private periodSelector (elementId: string, pricingPlanNumber: string): string {
        return `${this.pricingPlanSelector(elementId, pricingPlanNumber)} .plan-header .cost .period`;
    }

    private periodIndicatorSelector(elementId: string, pricingPlanNumber: string): string {
        return `${this.pricingPlanSelector(elementId, pricingPlanNumber)} .period-indicator`;
    }

    private descriptionSelector(elementId: string, pricingPlanNumber: string): string {
        return `${this.pricingPlanSelector(elementId, pricingPlanNumber)} .description`;
    }

    private mainFeaturesSelector(elementId: string, pricingPlanNumber: string): string {
        return `${this.pricingPlanSelector(elementId, pricingPlanNumber)} .features-wrapper`;
    }

    private ribbonSelector(elementId: string, pricingPlanNumber: string): string {
        return `${this.pricingPlanSelector(elementId, pricingPlanNumber)} .ribbon`;
    }

    private buttonSelector(elementId: string, pricingPlanNumber: string, hover: boolean = false): string {
        let selector = `${this.pricingPlanSelector(elementId, pricingPlanNumber)} .button-element`;
        if (hover) {
            selector += `:hover`;
        }
        return selector;
    }

}
